import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { RoomsHero } from "@/components/site/rooms/RoomsHero";
import { RoomsIntro } from "@/components/site/rooms/RoomsIntro";
import { RoomsFacts } from "@/components/site/rooms/RoomsFacts";
import { AccommodationDirectory } from "@/components/site/rooms/AccommodationDirectory";
import { RvSitesSection } from "@/components/site/rooms/RvSitesSection";
import { RvSiteOptions } from "@/components/site/rooms/RvSiteOptions";
import { CabinsSection } from "@/components/site/rooms/CabinsSection";
import { MotelSection } from "@/components/site/rooms/MotelSection";
import { TentCampingSection } from "@/components/site/rooms/TentCampingSection";
import { ExtendedStaysSection } from "@/components/site/rooms/ExtendedStaysSection";
import { RoomsAmenities } from "@/components/site/rooms/RoomsAmenities";
import { GroupStaysSection } from "@/components/site/rooms/GroupStaysSection";
import { RoomsCTA } from "@/components/site/rooms/RoomsCTA";
import { BUSINESS, CAMPGROUND_ID, FACEBOOK_URL, ROOMS_PATH } from "@/lib/site";
import { PHOTOS } from "@/components/site/images";

const TITLE = "RV Sites, Cabins & Rooms | Amigo Inn RV Park";
const DESCRIPTION =
  "Explore full-hookup RV sites, cabins, motel rooms, efficiency accommodations and tent camping at Amigo Inn & RV Park near Falcon Lake in Zapata, Texas.";
const CANONICAL = `https://amigoinn-rvpark.com${ROOMS_PATH}`;
const heroImage = PHOTOS.rvSite;
const OG_IMAGE = `https://amigoinn-rvpark.com${heroImage.src}`;

export const Route = createFileRoute("/rooms-and-parks")({
  component: RoomsAndParksPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "preload",
        as: "image",
        href: heroImage.src,
        imageSrcSet: heroImage.srcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          url: CANONICAL,
          description: DESCRIPTION,
          image: OG_IMAGE,
          isPartOf: {
            "@type": "WebSite",
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
          },
          about: { "@id": CAMPGROUND_ID },
          mainEntity: {
            "@id": CAMPGROUND_ID,
            "@type": ["Campground", "LodgingBusiness"],
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
            telephone: BUSINESS.phone,
            email: BUSINESS.email,
            sameAs: [FACEBOOK_URL],
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.street,
              addressLocality: BUSINESS.city,
              addressRegion: BUSINESS.region,
              postalCode: BUSINESS.postalCode,
              addressCountry: BUSINESS.country,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: BUSINESS.siteUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Rooms and Parks",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function RoomsAndParksPage() {
  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        <RoomsHero />
        <RoomsIntro />
        <RoomsFacts />
        <AccommodationDirectory />
        <RvSitesSection />
        <RvSiteOptions />
        <CabinsSection />
        <MotelSection />
        <TentCampingSection />
        <ExtendedStaysSection />
        <RoomsAmenities />
        <GroupStaysSection />
        <RoomsCTA />
      </main>

      <SiteFooter
        newsletterHeading="Stay Connected With Amigo Inn"
        newsletterDescription="Receive park news, seasonal information, event announcements, and updates from Amigo Inn & RV Park."
      />
      <MobileBookingBar />
    </div>
  );
}
